// GS1 DataBar Expandido Apilado para ZPL como GRÁFICO EXACTO (^GFA).
//
// Por qué: el ^BR nativo de Zebra (1) no admite FNC1 → un AI de largo
// variable solo puede ir al final, (2) reparte los segmentos por fila sin
// balancear (con 11 caracteres y 10 segmentos/fila salía una fila entera y
// otra con 1 solo segmento) y (3) en la TLP2844-Z se salía de la etiqueta.
// bwip-js (el mismo motor de la vista previa) codifica el símbolo con
// cualquier orden de AIs y entrega la matriz de módulos; se escala a un
// múltiplo ENTERO de dots (módulo = 3 dots = 0,375 mm a 203 dpi), así la
// impresora dibuja barras exactas — misma calidad que un símbolo nativo y
// la impresión es IDÉNTICA a la vista previa.

import bwipjs from 'bwip-js'

const ROW_MODULES = 34   // alto de fila del DataBar Expandido (34X, GS1)
const ROW_GAP = 3        // separador entre filas apiladas (bwip-js)

/** Filas de un símbolo apilado a partir de su alto en módulos. */
export const databarRows = pixy => Math.round((pixy + ROW_GAP) / (ROW_MODULES + ROW_GAP))

/**
 * bwip-js `raw()` NO devuelve pixx×pixy píxeles para el DataBar apilado:
 * entrega una fila de módulos por cada franja distinta (fila de barras de
 * 34X y las 3 filas del separador de 1X cada una) — 2 filas de barras = 5
 * franjas = 755 valores para 151×71. Devuelve las franjas como
 * [{ bits: '0101…', h: módulos }] (o el bitmap completo fila a fila si
 * viniera expandido). Asumir pixx×pixy aquí fue lo que aplastó el ^GFA.
 */
export function rawStripes(r) {
  const { pixx, pixy, pixs } = r
  const bits = i => Array.from(pixs.slice(i * pixx, (i + 1) * pixx)).map(v => (v ? '1' : '0')).join('')
  if (pixs.length === pixx * pixy) {
    return Array.from({ length: pixy }, (_, i) => ({ bits: bits(i), h: 1 }))
  }
  const rows = databarRows(pixy)
  const heights = []
  for (let i = 0; i < rows; i++) {
    heights.push(ROW_MODULES)
    if (i < rows - 1) heights.push(1, 1, 1)
  }
  if (pixs.length !== pixx * heights.length || heights.reduce((a, b) => a + b, 0) !== pixy) {
    throw new Error(`bwip-js raw: layout inesperado (${pixs.length} valores, ${pixx}x${pixy})`)
  }
  return heights.map((h, i) => ({ bits: bits(i), h }))
}

/**
 * Segmentos por fila que mejor reparten el símbolo: cabe en maxModules,
 * mínimo de filas y, a igual filas, el más angosto (= filas más parejas).
 * Devuelve { segments, pixx, pixy, rows }.
 */
export function bestDatabarSegments(elementString, maxModules = 253) {
  let best = null
  for (let segments = 4; segments <= 22; segments += 2) {
    let r
    try {
      r = bwipjs.raw('databarexpandedstacked', elementString, { segments })[0]
    } catch (e) { continue }
    if (!r || r.pixx > maxModules) continue
    const cand = { segments, pixx: r.pixx, pixy: r.pixy, rows: databarRows(r.pixy) }
    if (!best || cand.rows < best.rows || (cand.rows === best.rows && cand.pixx < best.pixx)) best = cand
  }
  return best || { segments: 4, pixx: 0, pixy: 0, rows: 0 }
}

/**
 * Símbolo dibujado con RECTÁNGULOS ^GB (una caja por corrida de módulos
 * negros), posicionados en absoluto desde (x0, y0). Es la forma más
 * compatible: ^GB existe en todo ZPL (el ^GFA también funciona en la
 * TLP2844-Z V45, verificado con DIAG 5; el aplastado fue por leer mal `raw`). Filas de módulos consecutivas iguales se agrupan en una banda
 * → ~2 filas de barras + separador = pocos cientos de ^GB.
 * Devuelve { zpl (con ^FO/^FS incluidos), widthDots, heightDots, segments, rows }.
 */
export function databarZplBars(elementString, { module = 3, maxWidthDots = 760, x0 = 0, y0 = 0 } = {}) {
  const pick = bestDatabarSegments(elementString, Math.floor(maxWidthDots / module))
  if (!pick.pixx) return null
  const r = bwipjs.raw('databarexpandedstacked', elementString, { segments: pick.segments })[0]
  const { pixx, pixy } = r
  const out = []
  let y = 0
  for (const st of rawStripes(r)) {
    // corridas de módulos negros en esta franja
    const h = st.h * module
    let x = 0
    while (x < pixx) {
      if (st.bits[x] !== '1') { x++; continue }
      let w = 1
      while (x + w < pixx && st.bits[x + w] === '1') w++
      // Borde = min(ancho, alto): un borde mayor que el ancho de la caja hace
      // que la 2844-Z ENSANCHE la caja (salían bandas negras); con el borde
      // igual al lado menor la caja queda rellena en cualquier firmware
      const bw = w * module
      out.push(`^FO${x0 + x * module},${y0 + y * module}^GB${bw},${h},${Math.min(bw, h)}^FS`)
      x += w
    }
    y += st.h
  }
  return {
    zpl: out.join(''),
    widthDots: pixx * module,
    heightDots: pixy * module,
    segments: pick.segments,
    rows: pick.rows,
    boxes: out.length
  }
}

/**
 * ^GFA del símbolo (sin ^FO ni ^FS): { zpl, widthDots, heightDots, segments, rows }.
 * (Alternativa compacta; la TLP2844-Z V45 lo imprimió aplastado → usar databarZplBars.)
 * module = dots por módulo (entero). maxWidthDots acota el ancho útil.
 */
export function databarZplGraphic(elementString, { module = 3, maxWidthDots = 760 } = {}) {
  const pick = bestDatabarSegments(elementString, Math.floor(maxWidthDots / module))
  if (!pick.pixx) return null
  const r = bwipjs.raw('databarexpandedstacked', elementString, { segments: pick.segments })[0]
  const widthDots = r.pixx * module
  const heightDots = r.pixy * module
  const bytesPerRow = Math.ceil(widthDots / 8)
  const hex = []
  for (const st of rawStripes(r)) {
    const row = new Uint8Array(bytesPerRow)
    for (let x = 0; x < r.pixx; x++) {
      if (st.bits[x] !== '1') continue
      for (let k = 0; k < module; k++) {
        const bit = x * module + k
        row[bit >> 3] |= 0x80 >> (bit & 7)
      }
    }
    let line = ''
    for (let b = 0; b < bytesPerRow; b++) line += row[b].toString(16).padStart(2, '0').toUpperCase()
    for (let k = 0; k < st.h * module; k++) hex.push(line)   // franja de h módulos = h·module filas de dots
  }
  const total = bytesPerRow * heightDots
  return {
    zpl: `^GFA,${total},${total},${bytesPerRow},${hex.join('')}`,
    widthDots,
    heightDots,
    segments: pick.segments,
    rows: pick.rows
  }
}
